import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { i18nErrors } from './messages';

export const useWizardValidation = (validators = {}) => {
    const { t } = useTranslation();

    const resolveError = useCallback(
        (errorResult) => {
            if (!errorResult) return null;

            if (typeof errorResult === 'string') {
                return t(i18nErrors[errorResult]);
            }

            if (errorResult?.code) {
                return t(i18nErrors[errorResult.code], errorResult.values || {});
            }

            return null;
        },
        [t]
    );

    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [submitAttempted, setSubmitAttempted] = useState(false);

    const touchField = useCallback((field) => {
        setTouched((prev) => ({ ...prev, [field]: true }));
    }, []);

    const validateField = useCallback(
        (field) => {
            const validator = validators[field];
            if (!validator) return;

            const errorCode = validator();

            setErrors((prev) => {
                const next = { ...prev };
                if (errorCode) {
                    next[field] = resolveError(errorCode);
                } else {
                    delete next[field];
                }
                return next;
            });
        },
        [validators, resolveError]
    );

    const validateAll = useCallback(() => {
        setSubmitAttempted(true);
        Object.keys(validators).forEach((field) => {
            validateField(field);
        });
    }, [validators, validateField]);

    const isValid = useCallback(() => {
        const nextErrors = {};

        Object.keys(validators).forEach((field) => {
            const validator = validators[field];
            if (!validator) return;

            const errorCode = validator();
            if (errorCode) {
                nextErrors[field] = resolveError(errorCode);
            }
        });

        return Object.keys(nextErrors).length === 0;
    }, [validators, resolveError]);

    const shouldShowError = useCallback(
        (field) =>
            Boolean(errors[field] && (touched[field] || submitAttempted)),
        [errors, touched, submitAttempted]
    );

    return {
        errors,
        touched,
        submitAttempted,

        touchField,
        validateField,
        validateAll,
        isValid,
        shouldShowError,
    };
};
